/**
 * GoogleAdsPage — Google Ads Connector (Data Manager API) pro Firma.
 * OAuth-Assistent, Conversion-Action-Konfiguration, Test, Queue-Monitor.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CircleDollarSign, Loader2, Copy, Link2, FlaskConical, RefreshCw, CheckCircle2, XCircle, ExternalLink, AlertTriangle, Sparkles } from "lucide-react";
import PageHelp from "@/components/PageHelp";
import CampaignManager from "@/components/googleads/CampaignManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MASK = "********";

const QUEUE_BADGE = {
  done: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending: "bg-blue-100 text-blue-700 border-blue-200",
  retry: "bg-amber-100 text-amber-700 border-amber-200",
  failed: "bg-red-100 text-red-700 border-red-200",
};

export default function GoogleAdsPage() {
  const [params] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [redirectUri, setRedirectUri] = useState("");
  const [queue, setQueue] = useState(null);
  const [diag, setDiag] = useState(null);
  const [aiDiag, setAiDiag] = useState(null);
  const [aiDiagLoading, setAiDiagLoading] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [setupChecks, setSetupChecks] = useState(null);
  const [busy, setBusy] = useState("");

  const runSetupCheck = async () => {
    setBusy("check");
    setSetupChecks(null);
    try {
      const r = await axios.get(`${API}/google-ads/${companyId}/setup-check`, { withCredentials: true });
      setSetupChecks(r.data.checks || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Setup-Check fehlgeschlagen"); }
    setBusy("");
  };

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => { setCompanies(r.data || []); if (r.data?.length) setCompanyId(r.data[0].id); })
      .catch(() => {});
    axios.get(`${API}/google-ads/oauth/redirect-uri`, { withCredentials: true })
      .then((r) => setRedirectUri(r.data.redirect_uri)).catch(() => {});
    if (params.get("connected") === "google_ads") toast.success("Google verbunden — Refresh-Token gespeichert!");
    if (params.get("connect_error")) toast.error(`Google-Verbindung fehlgeschlagen: ${params.get("connect_error")}`);
  }, [params]);

  const load = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const [st, q, dg] = await Promise.all([
        axios.get(`${API}/google-ads/${cid}/settings`, { withCredentials: true }),
        axios.get(`${API}/google-ads/${cid}/queue`, { withCredentials: true }),
        axios.get(`${API}/google-ads/${cid}/conversion-diagnosis`, { withCredentials: true }).catch(() => null),
      ]);
      setS(st.data);
      setDiag(dg?.data || null);
      setForm({
        enabled: st.data.enabled,
        gcp_project_id: st.data.central?.gcp_project_id || st.data.gcp_project_id,
        client_id: st.data.central?.client_id || st.data.client_id,
        client_secret: st.data.central?.has_client_secret ? MASK : "",
        operating_customer_id: st.data.operating_customer_id,
        login_customer_id: st.data.login_customer_id,
        campaign_filter: st.data.campaign_filter,
        qualified_lead_action_id: st.data.qualified_lead_action_id,
        paid_revenue_action_id: st.data.paid_revenue_action_id,
      });
      setQueue(q.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    }
  }, []);

  useEffect(() => { load(companyId); }, [companyId, load]);

  const save = async () => {
    setBusy("save");
    try {
      await axios.put(`${API}/google-ads/${companyId}/settings`, form, { withCredentials: true });
      toast.success("Gespeichert");
      load(companyId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(""); }
  };

  const connect = async () => {
    setBusy("connect");
    try {
      await axios.put(`${API}/google-ads/${companyId}/settings`, form, { withCredentials: true });
      const r = await axios.get(`${API}/google-ads/${companyId}/oauth/start`, { withCredentials: true });
      window.location.href = r.data.redirect_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "OAuth-Start fehlgeschlagen");
      setBusy("");
    }
  };

  const loadAccounts = async () => {
    setBusy("accounts");
    try {
      const r = await axios.get(`${API}/google-ads/accessible-customers`, { withCredentials: true });
      setAccounts(r.data.accounts || []);
      if (!r.data.accounts?.length) toast.info("Keine Google-Ads-Konten gefunden");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konten laden fehlgeschlagen");
    } finally { setBusy(""); }
  };

  const disconnect = async (scope = "company") => {
    const msg = scope === "central"
      ? "Zentrale Google-Verbindung wirklich trennen? Das betrifft ALLE Firmen — danach muss einmal neu mit Google verbunden werden."
      : "Google-Ads-Konto (Kunden-ID) wirklich von dieser Firma entfernen? Die zentrale Google-Verbindung und die Kampagnen bleiben bestehen.";
    if (!window.confirm(msg)) return;
    setBusy("disconnect");
    try {
      const r = await axios.delete(`${API}/google-ads/${companyId}/connection?scope=${scope}`, { withCredentials: true });
      toast.success(r.data.message);
      load(companyId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trennen fehlgeschlagen");
    } finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try {
      const r = await axios.post(`${API}/google-ads/${companyId}/test`, {}, { withCredentials: true });
      toast.success(r.data.message);
      load(companyId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen");
      load(companyId);
    } finally { setBusy(""); }
  };

  const F = (key, label, placeholder, type = "text") => (
    <div>
      <label className="text-xs font-medium">{label}</label>
      <Input
        data-testid={`gads-${key}`}
        type={type}
        value={form[key] || ""}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        className="mt-1"
      />
    </div>
  );

  return (
    <div className="p-6 space-y-5" data-testid="google-ads-page">
      <PageHelp
        title="Google Ads Connector"
        text="Die Google-Verbindung (GCP-Projekt, OAuth-Client) wird einmal zentral für alle Firmen der Gruppe eingerichtet. Pro Firma wird nur das eigene Google-Ads-Konto (Kunden-ID) und die Conversion-Actions hinterlegt. Offline-Conversions laufen über die Google Data Manager API, Kampagnen-Verwaltung über die Google Ads API v24 (seit 9.9.2026 ohne Developer-Token)."
      />
      <div className="flex flex-wrap items-center gap-3">
        <CircleDollarSign className="w-6 h-6 text-[#2563EB]" />
        <h1 className="text-2xl font-bold">Google Ads</h1>
        <div className="flex-1" />
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-56" data-testid="gads-company-select"><SelectValue placeholder="Firma" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {s && (
        <Tabs defaultValue="campaigns" className="space-y-4">
          <TabsList data-testid="gads-tabs">
            <TabsTrigger value="campaigns" data-testid="gads-tab-campaigns">Kampagnen</TabsTrigger>
            <TabsTrigger value="settings" data-testid="gads-tab-settings">Verbindung &amp; Conversions</TabsTrigger>
          </TabsList>

          <TabsContent value="campaigns">
            <CampaignManager companyId={companyId} />
          </TabsContent>

          <TabsContent value="settings" className="space-y-5">
          <Card data-testid="gads-central-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex flex-wrap items-center gap-2">
                Zentrale Google-Verbindung
                <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-300">gilt für alle Firmen</Badge>
                {s.central?.has_refresh_token ? (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" data-testid="gads-connected-badge">Google verbunden</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Nicht verbunden</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-[var(--studio-text-muted)]">
                GCP-Projekt, OAuth-Client und die Google-Verbindung werden <b>einmal zentral</b> für die ganze
                InvictaX-Gruppe eingerichtet. Pro Firma kommt nur noch das eigene Google-Ads-Konto (Kunden-ID) dazu.
              </p>
              <div className="rounded-lg border border-[var(--studio-border)] bg-[var(--studio-bg)] p-3 text-xs flex flex-wrap items-center gap-2">
                <Link2 className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium">OAuth Redirect-URI</span> (exakt so in der Google Cloud Console eintragen):
                <code className="bg-white border border-[var(--studio-border)] rounded px-2 py-0.5 break-all" data-testid="gads-redirect-uri">{redirectUri}</code>
                <Button size="sm" variant="ghost" className="h-6 px-2" data-testid="gads-copy-uri" onClick={() => { navigator.clipboard.writeText(redirectUri); toast.success("Kopiert"); }}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="gads-portal-links">
                {[
                  { label: "Google Cloud Console (OAuth-Client anlegen)", url: "https://console.cloud.google.com/apis/credentials" },
                  { label: "Data Manager API aktivieren (Pflicht!)", url: "https://console.cloud.google.com/apis/library/datamanager.googleapis.com" },
                ].map((l) => (
                  <Button key={l.url} size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => window.open(l.url, "_blank", "noopener")}>
                    <ExternalLink className="w-3 h-3 mr-1" /> {l.label}
                  </Button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {F("gcp_project_id", "GCP-Projekt-ID *", "invictaflow-marketing")}
                {F("client_id", "OAuth Client-ID *", "xxxx.apps.googleusercontent.com")}
                {F("client_secret", "OAuth Client-Secret *", "GOCSPX-…", "password")}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={save} disabled={busy === "save"} data-testid="gads-save-btn">
                  {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Speichern
                </Button>
                <Button variant="outline" onClick={connect} disabled={busy === "connect" || !form.client_id} className="gap-1" data-testid="gads-connect-btn">
                  {busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Mit Google verbinden
                </Button>
                {s.central?.has_refresh_token && (
                  <Button variant="outline" onClick={() => disconnect("central")} disabled={busy === "disconnect"} className="gap-1 text-red-600 border-red-300 hover:bg-red-50" data-testid="gads-disconnect-central-btn">
                    {busy === "disconnect" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Zentrale Verbindung trennen
                  </Button>
                )}
                {s.central?.connected_at && (
                  <span className="flex items-center gap-1 text-xs text-[var(--studio-text-muted)]">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    Verbunden seit {new Date(s.central.connected_at).toLocaleString("de-CH")}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="gads-company-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex flex-wrap items-center gap-2">
                Firmen-Einstellungen — {companies.find((c) => c.id === companyId)?.name || ""}
                {s.configured ? (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" data-testid="gads-configured-badge">Konfiguriert</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Unvollständig</Badge>
                )}
                <div className="flex-1" />
                <div className="flex items-center gap-2 text-xs font-normal">
                  Aktiv
                  <Switch data-testid="gads-enabled-switch" checked={!!form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {[
                  { label: "Google Ads Conversions (Action-IDs)", url: "https://ads.google.com/aw/conversions" },
                  { label: "Google Ads Konto (Kunden-ID oben rechts)", url: "https://ads.google.com/aw/overview" },
                ].map((l) => (
                  <Button key={l.url} size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => window.open(l.url, "_blank", "noopener")}>
                    <ExternalLink className="w-3 h-3 mr-1" /> {l.label}
                  </Button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-[var(--studio-border)] p-2.5">
                <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={loadAccounts}
                  disabled={busy === "accounts" || !s.central?.has_refresh_token}
                  title={!s.central?.has_refresh_token ? "Zuerst zentral mit Google verbinden" : "Listet alle Konten, auf die dein Google-Login Zugriff hat"}
                  data-testid="gads-load-accounts-btn">
                  {busy === "accounts" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Konto wählen (von Google laden)
                </Button>
                {accounts && (accounts.length ? (
                  <Select onValueChange={(v) => {
                    const acc = accounts.find((a) => a.id === v);
                    if (acc?.manager) {
                      setForm((f) => ({ ...f, login_customer_id: v }));
                      toast.warning(`${v} ist ein MCC/Verwaltungskonto — als «MCC Login-Kunden-ID» übernommen. Wähle zusätzlich das Unterkonto der Firma als Kunden-ID.`, { duration: 9000 });
                      return;
                    }
                    setForm((f) => ({ ...f, operating_customer_id: v, ...(acc?.parent_mcc ? { login_customer_id: acc.parent_mcc } : {}) }));
                    toast.success(acc?.parent_mcc
                      ? `Kunden-ID ${v} + MCC Login-ID ${acc.parent_mcc} übernommen — jetzt «Speichern» klicken`
                      : `Kunden-ID ${v} übernommen — jetzt «Speichern» klicken`);
                  }}>
                    <SelectTrigger className="h-8 w-[22rem] text-xs" data-testid="gads-account-select">
                      <SelectValue placeholder={`${accounts.length} Konten gefunden — auswählen…`} />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id} data-testid={`gads-account-option-${a.id}`}>
                          {a.parent_mcc ? "↳ " : ""}{a.name} — {a.id}{a.manager ? " (MCC/Verwaltungskonto)" : ""}{a.currency ? ` · ${a.currency}` : ""}{a.parent_mcc ? " · Unterkonto" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-xs text-amber-600">Keine Konten gefunden — zuerst unter ads.google.com ein Konto für diese Firma erstellen.</span>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {F("operating_customer_id", "Google Ads Kunden-ID * (ohne Bindestriche)", "1234567890")}
                {F("login_customer_id", "MCC Login-Kunden-ID (optional)", "1234567890")}
                {F("qualified_lead_action_id", "Conversion-Action-ID: Qualified Lead", "numerisch")}
                {F("paid_revenue_action_id", "Conversion-Action-ID: Paid Revenue", "numerisch")}
                <div className="sm:col-span-2">
                  {F("campaign_filter", "Kampagnen-Filter (bei geteiltem Konto)", "z.B. ReinMove  oder  -ReinMove")}
                  <p className="text-[11px] text-[var(--studio-text-muted)] mt-1">
                    Zeigt nur Kampagnen, deren Name den Begriff enthält (mehrere mit Komma). Mit «-Begriff» ausschliessen —
                    z.B. ReinMove: <code>ReinMove</code> · StrongFit: <code>-ReinMove</code>. Gilt für Kampagnen-Manager, Cockpit und Kosten-Statistik.
                    Neue Kampagnen erhalten den Begriff automatisch als Namens-Präfix.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={save} disabled={busy === "save"} data-testid="gads-save-company-btn">
                  {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Speichern
                </Button>
                <Button variant="outline" onClick={test} disabled={busy === "test" || !s.testable} className="gap-1" data-testid="gads-test-btn">
                  {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Verbindung testen
                </Button>
                <Button variant="outline" onClick={runSetupCheck} disabled={busy === "check" || !s.testable}
                  className="gap-1 text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="gads-setup-check-btn"
                  title="Prüft jeden Schritt einzeln gegen die Google-API: Konto, Nutzungsbedingungen, Conversion-Actions, Upload">
                  {busy === "check" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Google-Setup prüfen
                </Button>
                {s.operating_customer_id && (
                  <Button variant="outline" onClick={() => disconnect("company")} disabled={busy === "disconnect"} className="gap-1 text-red-600 border-red-300 hover:bg-red-50" data-testid="gads-disconnect-btn">
                    {busy === "disconnect" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Konto von Firma entfernen
                  </Button>
                )}
                {s.last_test_at && (
                  <span className="flex items-center gap-1 text-xs text-[var(--studio-text-muted)]" data-testid="gads-last-test">
                    {s.last_test_ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                    Letzter Test: {new Date(s.last_test_at).toLocaleString("de-CH")} {s.last_test_error ? `— ${s.last_test_error}` : ""}
                  </span>
                )}
              </div>
              {setupChecks && (
                <div className="mt-3 border rounded-lg divide-y" data-testid="gads-setup-check-results">
                  <div className="px-3 py-2 flex items-center justify-between bg-slate-50 rounded-t-lg">
                    <p className="text-xs font-semibold">Google-Setup-Check — {setupChecks.filter((c) => c.ok).length}/{setupChecks.length} Punkte OK</p>
                    <button type="button" className="text-[11px] text-slate-400 hover:text-slate-600" onClick={() => setSetupChecks(null)}>Schliessen</button>
                  </div>
                  {setupChecks.map((c) => (
                    <div key={c.id} className="px-3 py-2 flex items-start gap-2" data-testid={`setup-check-${c.id}`}>
                      {c.ok
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        : <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />}
                      <div className="min-w-0">
                        <p className="text-xs font-medium">{c.label}</p>
                        {c.detail && <p className={`text-[11px] ${c.ok ? "text-slate-400" : "text-red-600"}`}>{c.detail}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {diag && (
            <Card data-testid="gads-diagnosis-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex flex-wrap items-center gap-2">
                  Conversion-Diagnose
                  {diag.ready ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Messkette bereit</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-red-600 border-red-300">Messkette unterbrochen</Badge>
                  )}
                  <span className="text-xs font-normal text-[var(--studio-text-muted)]">Warum zeigen die Kampagnen 0 Conversions?</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {diag.checks.map((c) => (
                  <div key={c.id} className="flex items-start gap-2 text-sm" data-testid={`gads-diag-${c.id}`}>
                    {c.ok === true ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      : c.ok === false ? <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />}
                    <div className="min-w-0">
                      <p className="font-medium text-xs">
                        {c.title}
                        {typeof c.count === "number" && <span className="text-[var(--studio-text-muted)] font-normal"> — {c.count}</span>}
                        {c.counts && <span className="text-[var(--studio-text-muted)] font-normal"> — done: {c.counts.done} · pending: {c.counts.pending} · failed: {c.counts.failed}</span>}
                      </p>
                      {c.hint && <p className="text-xs text-[var(--studio-text-muted)]">{c.hint}</p>}
                    </div>
                  </div>
                ))}
                {!diag.ready && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.location.assign("/web-analytics")} data-testid="gads-diag-goto-snippet">
                      <ExternalLink className="w-3 h-3 mr-1" /> Zum Tracking-Snippet (Website Analytics)
                    </Button>
                  </div>
                )}
                <div className="pt-2 border-t border-[var(--studio-border)]">
                  <Button size="sm" variant="outline" className="h-8 text-xs gap-1" disabled={aiDiagLoading}
                    onClick={async () => {
                      setAiDiagLoading(true);
                      try {
                        const r = await axios.post(`${API}/google-ads/${companyId}/ai/conversion-diagnosis`, {}, { withCredentials: true });
                        setAiDiag(r.data);
                      } catch (e) { toast.error(e.response?.data?.detail || "KI-Diagnose fehlgeschlagen"); }
                      finally { setAiDiagLoading(false); }
                    }}
                    data-testid="gads-ai-diagnosis-btn">
                    {aiDiagLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    KI-Diagnose: Schritt-für-Schritt zur Lösung
                  </Button>
                  {aiDiag && (
                    <div className="mt-3 space-y-2" data-testid="gads-ai-diagnosis-panel">
                      {aiDiag.summary && <p className="text-xs text-[var(--studio-text-muted)]">{aiDiag.summary}</p>}
                      {aiDiag.root_cause && (
                        <p className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                          🎯 Hauptursache: {aiDiag.root_cause}
                        </p>
                      )}
                      {(aiDiag.steps || []).map((st, i) => (
                        <div key={i} className={`flex items-start gap-2 border rounded-lg px-3 py-2 text-sm ${st.done ? "border-emerald-200 bg-emerald-50/50" : "border-[var(--studio-border)]"}`} data-testid={`gads-ai-diag-step-${i}`}>
                          <span className={`shrink-0 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${st.done ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                            {st.done ? "✓" : st.nr || i + 1}
                          </span>
                          <div className="min-w-0">
                            <div className="font-medium text-xs flex items-center flex-wrap gap-1.5">
                              {st.title}
                              {st.where && <Badge variant="outline" className="text-[9px]">{st.where}</Badge>}
                            </div>
                            <p className="text-xs text-[var(--studio-text-muted)] mt-0.5">{st.action}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card data-testid="gads-queue-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                Conversion-Queue
                {queue && Object.entries(queue.counts).map(([k, v]) => (
                  <Badge key={k} variant="outline" className={`text-[10px] ${QUEUE_BADGE[k]}`}>{k}: {v}</Badge>
                ))}
                <div className="flex-1" />
                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => load(companyId)} data-testid="gads-queue-refresh">
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {queue?.items?.length === 0 && (
                <p className="text-xs text-[var(--studio-text-muted)]" data-testid="gads-queue-empty">
                  Noch keine Conversions. Deine Systeme senden Events an: <code className="bg-[var(--studio-bg)] px-1 rounded">POST /api/google-ads/{"{firma-id}"}/conversions</code> mit {"{ref_id, kind: lead|purchase, value, gclid, email, phone, consent}"}
                </p>
              )}
              {queue?.items?.map((q) => (
                <div key={q.transaction_id} className="flex flex-wrap items-center gap-2 border border-[var(--studio-border)] rounded-lg px-3 py-1.5 text-xs" data-testid={`gads-queue-${q.transaction_id}`}>
                  <code className="font-medium">{q.transaction_id}</code>
                  <Badge variant="outline" className={`text-[9px] ${QUEUE_BADGE[q.status] || ""}`}>{q.status}</Badge>
                  {q.value != null && <span>{q.currency} {q.value}</span>}
                  <span className="text-[var(--studio-text-muted)]">Versuche: {q.attempts}</span>
                  {q.last_error && <span className="text-red-500 truncate max-w-xs">{q.last_error}</span>}
                </div>
              ))}
            </CardContent>
          </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
